import React from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { PageHero } from "../components/PageHero";
import { Reveal } from "../components/Reveal";
import { Newspaper, Video, FileText, FolderSearch, ClipboardList, CalendarDays, Clock, ArrowRight, Download, Play, CheckCircle2, MapPin } from "lucide-react";

/* ========== BLOG ========== */
const BLOG_POSTS = [
  { title: "The Real ROI of Enterprise AI Training in 2026", desc: "How Fortune-500 L&D teams measure value beyond completion rates — frameworks, KPIs and case studies.", author: "Karthik Subramanian", date: "Mar 22, 2026", tag: "AI & Strategy", img: "https://images.unsplash.com/photo-1677442136019-21780ecad995?w=800&q=80" },
  { title: "Why Skills-First Hiring is Replacing Degree-First Hiring", desc: "Inside the shift to capability-based talent acquisition and how Indian enterprises are leading the change.", author: "Meera Nair", date: "Mar 14, 2026", tag: "Talent", img: "https://images.unsplash.com/photo-1521737604893-d14cc237f11d?w=800&q=80" },
  { title: "Building a Cloud Centre of Excellence in 90 Days", desc: "A practical, week-by-week playbook for L&D and platform engineering teams.", author: "Rahul Mehta", date: "Feb 28, 2026", tag: "Cloud", img: "https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=800&q=80" },
  { title: "LXP vs LMS: Which One Does Your Enterprise Actually Need?", desc: "Differences, overlaps and how leading enterprises are choosing in 2026.", author: "Priya Sharma", date: "Feb 11, 2026", tag: "Learning Tech", img: "https://images.unsplash.com/photo-1517694712202-14dd9538aa97?w=800&q=80" },
  { title: "Cybersecurity Awareness in the Era of Generative AI", desc: "How to update your annual awareness program for prompt-injection, deepfakes and AI-driven phishing.", author: "Anjali Kapoor", date: "Jan 25, 2026", tag: "Security", img: "https://images.unsplash.com/photo-1550751827-4bd374c3f58b?w=800&q=80" },
  { title: "The State of Indian L&D 2026: Benchmarks Report", desc: "Spend, modality mix, completion rates and outcome data from 240+ enterprise L&D leaders.", author: "SkillsGain Research", date: "Jan 08, 2026", tag: "Research", img: "https://images.unsplash.com/photo-1556761175-5973dc0f32e7?w=800&q=80" }
];

export const BlogPage = () => (
  <div className="bg-white min-h-screen">
    <Header/>
    <PageHero eyebrow="BLOG" title="Insights, ideas &amp; playbooks" subtitle="Original research and field-tested perspectives for L&amp;D heads and decision-makers."/>
    <section className="max-w-7xl mx-auto px-4 py-16">
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {BLOG_POSTS.map((p, i) => (
          <Reveal key={p.title} variant="up" delay={(i % 6) * 60}>
            <article className="sg-card group bg-white border border-gray-100 rounded-2xl overflow-hidden h-full flex flex-col">
              <div className="h-44 overflow-hidden">
                <img src={p.img} alt={p.title} className="sg-img-zoom w-full h-full object-cover"/>
              </div>
              <div className="p-6 flex flex-col flex-1">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-[11px] bg-[#6BBE32]/15 text-[#4F9622] px-2 py-1 rounded font-semibold">{p.tag}</span>
                  <span className="text-xs text-gray-500">{p.date}</span>
                </div>
                <h3 className="text-[#15348C] group-hover:text-[#6BBE32] transition-colors leading-snug" style={{fontSize:"1.15rem"}}>{p.title}</h3>
                <p className="text-sm text-gray-600 mt-2 leading-relaxed line-clamp-3">{p.desc}</p>
                <div className="mt-5 pt-4 border-t border-gray-100 flex items-center justify-between text-sm">
                  <span className="text-gray-600">{p.author}</span>
                  <Link to="/contact" className="text-[#F57C20] font-semibold sg-link inline-flex items-center gap-1">Read article <ArrowRight size={13}/></Link>
                </div>
              </div>
            </article>
          </Reveal>
        ))}
      </div>
    </section>
    <Footer/>
  </div>
);

/* ========== WEBINARS ========== */
const WEBINARS = [
  { title: "Scaling Cloud Certifications across 5,000+ Engineers", date: "Apr 18, 2026", time: "4:00 PM IST", host: "Vikram Iyer", img: "https://images.unsplash.com/photo-1551836022-d5d88e9218df?w=800&q=80", status: "upcoming" },
  { title: "AI Agent Workshops for Engineering Leaders", date: "Apr 24, 2026", time: "11:00 AM IST", host: "Priya Sharma", img: "https://images.unsplash.com/photo-1677442136019-21780ecad995?w=800&q=80", status: "upcoming" },
  { title: "Designing the L&D Operating Model of 2026", date: "Mar 12, 2026", time: "Replay", host: "Meera Nair", img: "https://images.unsplash.com/photo-1556761175-5973dc0f32e7?w=800&q=80", status: "replay" },
  { title: "From LMS to LXP — A Migration Playbook", date: "Feb 28, 2026", time: "Replay", host: "Karthik S.", img: "https://images.unsplash.com/photo-1517694712202-14dd9538aa97?w=800&q=80", status: "replay" }
];

export const WebinarsPage = () => (
  <div className="bg-white min-h-screen">
    <Header/>
    <PageHero eyebrow="WEBINARS" title="Live sessions with industry experts" subtitle="Join weekly conversations on AI, Cloud, Cybersecurity and modern L&amp;D — free for the SkillsGain community."/>
    <section className="max-w-7xl mx-auto px-4 py-16">
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {WEBINARS.map((w, i) => (
          <Reveal key={w.title} variant="up" delay={(i % 4) * 60}>
            <div className="sg-card group bg-white border border-gray-100 rounded-2xl overflow-hidden h-full flex flex-col">
              <div className="relative h-36 overflow-hidden">
                <img src={w.img} alt={w.title} className="sg-img-zoom w-full h-full object-cover"/>
                <div className="absolute inset-0 bg-gradient-to-t from-[#15348C]/85 to-transparent"/>
                <span className={`absolute top-3 left-3 text-[10px] font-bold tracking-wider px-2 py-1 rounded-full uppercase ${w.status==="upcoming" ? "bg-[#F57C20] text-white" : "bg-white text-[#15348C]"}`}>{w.status}</span>
                <div className="absolute bottom-2 left-3 text-white text-xs">{w.date} • {w.time}</div>
              </div>
              <div className="p-5 flex-1 flex flex-col">
                <h3 className="text-[#15348C] leading-snug" style={{fontSize:"1.05rem"}}>{w.title}</h3>
                <p className="text-xs text-gray-500 mt-2 flex-1">Host: {w.host}</p>
                <button className="mt-4 inline-flex items-center gap-1 text-sm text-[#F57C20] font-semibold sg-link">
                  {w.status==="upcoming" ? "Reserve seat" : "Watch replay"} <ArrowRight size={13}/>
                </button>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
    <Footer/>
  </div>
);

/* ========== WHITEPAPERS ========== */
const WHITEPAPERS = [
  { title: "The Enterprise GenAI Adoption Maturity Model", pages: 28, desc: "A five-stage maturity framework for enterprises adopting Generative AI at scale." },
  { title: "State of Cloud Certifications 2026", pages: 34, desc: "Pass rates, market demand, salary benchmarks and skill gaps across AWS, Azure and GCP." },
  { title: "Designing High-Engagement Learning Programs", pages: 22, desc: "Research-backed practices for completion rates above 90% in voluntary enterprise programs." },
  { title: "From Talent Gap to Talent Pipeline", pages: 30, desc: "How leading enterprises are integrating learning with succession and internal mobility." }
];

export const WhitepapersPage = () => (
  <div className="bg-white min-h-screen">
    <Header/>
    <PageHero eyebrow="WHITEPAPERS" title="Research-grade reading for L&amp;D leaders" subtitle="In-depth research on emerging technologies, learning science and enterprise capability building."/>
    <section className="max-w-7xl mx-auto px-4 py-16">
      <div className="grid md:grid-cols-2 gap-6">
        {WHITEPAPERS.map((w, i) => (
          <Reveal key={w.title} variant="up" delay={i * 60}>
            <div className="sg-card bg-white border border-gray-100 rounded-2xl p-6 lg:p-8 flex items-start gap-5 h-full">
              <div className="w-16 h-20 rounded-lg bg-[#15348C] text-white flex flex-col items-center justify-center text-xs font-bold shrink-0">
                PDF<br/><span className="text-[10px] font-normal opacity-80">{w.pages} pages</span>
              </div>
              <div className="flex-1">
                <h3 className="text-[#15348C]">{w.title}</h3>
                <p className="text-sm text-gray-600 mt-2 leading-relaxed">{w.desc}</p>
                <Link to="/contact" className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-[#F57C20] sg-link">
                  <Download size={14}/> Download whitepaper
                </Link>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
    <Footer/>
  </div>
);

/* ========== CASE STUDIES ========== */
const CASES = [
  { client: "Global IT Services (Fortune 500)", title: "Trained 4,200 engineers on AWS in 11 months", outcome: "38% L&D cost reduction • 92% completion rate", img: "https://images.unsplash.com/photo-1556761175-5973dc0f32e7?w=900&q=80" },
  { client: "Tier-1 Indian Bank", title: "Enterprise-wide GenAI literacy in 6 weeks", outcome: "14,000 learners onboarded • 4.8/5 NPS", img: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=900&q=80" },
  { client: "Telecom Operator", title: "Cloud migration capability build-out", outcome: "600+ engineers certified • 6-month go-live", img: "https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=900&q=80" }
];

export const CaseStudiesPage = () => (
  <div className="bg-white min-h-screen">
    <Header/>
    <PageHero eyebrow="CASE STUDIES" title="Outcomes our clients have delivered" subtitle="Real enterprise transformations powered by SkillsGain learning programs."/>
    <section className="max-w-7xl mx-auto px-4 py-16 space-y-8">
      {CASES.map((c, i) => (
        <Reveal key={c.title} variant="up" delay={i * 80}>
          <div className="sg-card grid lg:grid-cols-2 gap-0 bg-white border border-gray-100 rounded-2xl overflow-hidden">
            <div className={`h-64 lg:h-auto ${i % 2 ? "lg:order-2" : ""}`}>
              <img src={c.img} alt={c.title} className="w-full h-full object-cover"/>
            </div>
            <div className="p-8 lg:p-10 flex flex-col justify-center">
              <div className="text-xs font-bold tracking-widest text-[#F57C20] mb-2">{c.client}</div>
              <h3 className="text-[#15348C]" style={{fontSize:"1.5rem"}}>{c.title}</h3>
              <div className="mt-3 text-[#4F9622] font-semibold text-sm flex items-center gap-1"><CheckCircle2 size={16}/> {c.outcome}</div>
              <Link to="/contact" className="mt-5 inline-flex items-center gap-1 text-sm text-[#F57C20] font-semibold sg-link">Read full case study <ArrowRight size={13}/></Link>
            </div>
          </div>
        </Reveal>
      ))}
    </section>
    <Footer/>
  </div>
);

/* ========== PRACTICE TESTS ========== */
const TESTS = [
  { name: "AWS Solutions Architect – Associate", q: 65, time: "130 min", attempts: "Unlimited", level: "Intermediate" },
  { name: "AWS Solutions Architect – Professional", q: 75, time: "180 min", attempts: "Unlimited", level: "Advanced" },
  { name: "Microsoft Azure Fundamentals (AZ-900)", q: 60, time: "60 min", attempts: "Unlimited", level: "Beginner" },
  { name: "Google Professional Cloud Architect", q: 60, time: "120 min", attempts: "Unlimited", level: "Advanced" },
  { name: "Certified Kubernetes Administrator (CKA)", q: 50, time: "120 min", attempts: "Unlimited", level: "Intermediate" },
  { name: "PMP — Project Management Professional", q: 200, time: "230 min", attempts: "Unlimited", level: "Advanced" },
  { name: "Certified Scrum Master (CSM)", q: 50, time: "60 min", attempts: "Unlimited", level: "Foundation" },
  { name: "CISSP", q: 150, time: "240 min", attempts: "Unlimited", level: "Expert" }
];

export const PracticeTestsPage = () => (
  <div className="bg-white min-h-screen">
    <Header/>
    <PageHero eyebrow="PRACTICE TESTS" title="Exam-ready in days, not months" subtitle="Mock tests aligned to the latest syllabi for 80+ certifications."/>
    <section className="max-w-7xl mx-auto px-4 py-16">
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {TESTS.map((t, i) => (
          <Reveal key={t.name} variant="up" delay={(i % 6) * 50}>
            <div className="sg-card group bg-white border border-gray-100 rounded-2xl p-6 h-full">
              <span className="text-[11px] bg-[#15348C] text-white px-2 py-1 rounded font-semibold">{t.level}</span>
              <h3 className="mt-3 text-[#15348C] group-hover:text-[#6BBE32] transition-colors leading-snug" style={{fontSize:"1.05rem"}}>{t.name}</h3>
              <div className="mt-4 grid grid-cols-3 gap-3 text-center">
                <Stat label="Questions" value={t.q}/>
                <Stat label="Time" value={t.time}/>
                <Stat label="Attempts" value={t.attempts}/>
              </div>
              <button className="mt-5 w-full bg-[#15348C] hover:bg-[#0A1F5C] text-white font-semibold py-2.5 rounded-lg transition-colors inline-flex items-center justify-center gap-2">
                <Play size={14}/> Start Practice Test
              </button>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
    <Footer/>
  </div>
);

const Stat = ({ label, value }) => (
  <div className="bg-[#F8F9FA] rounded-lg p-2">
    <div className="text-sm font-bold text-[#15348C]">{value}</div>
    <div className="text-[10px] uppercase tracking-wider text-gray-500">{label}</div>
  </div>
);

/* ========== EVENTS ========== */
const EVENTS = [
  { title: "SkillsGain GenAI Summit 2026", date: "May 18, 2026", city: "Bengaluru", venue: "Taj West End", img: "https://images.unsplash.com/photo-1505373877841-8d25f7d46678?w=900&q=80" },
  { title: "Cloud Engineers Meetup — Q2", date: "Apr 27, 2026", city: "Hyderabad", venue: "T-Hub", img: "https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=900&q=80" },
  { title: "AWS Certification Camp", date: "May 04, 2026", city: "Pune", venue: "SkillsGain Campus", img: "https://images.unsplash.com/photo-1431540015161-0bf868a2d407?w=900&q=80" },
  { title: "LXP Product Showcase", date: "Jun 11, 2026", city: "Virtual", venue: "Live on Zoom", img: "https://images.unsplash.com/photo-1591115765373-5207764f72e7?w=900&q=80" }
];

export const EventsPage = () => (
  <div className="bg-white min-h-screen">
    <Header/>
    <PageHero eyebrow="EVENTS" title="Conferences, meetups &amp; certification camps" subtitle="Meet the community in person and online — across India and beyond."/>
    <section className="max-w-7xl mx-auto px-4 py-16">
      <div className="grid md:grid-cols-2 gap-6">
        {EVENTS.map((e, i) => (
          <Reveal key={e.title} variant="up" delay={(i % 4) * 60}>
            <div className="sg-card group grid grid-cols-3 bg-white border border-gray-100 rounded-2xl overflow-hidden h-full">
              <div className="col-span-1 relative">
                <img src={e.img} alt={e.title} className="sg-img-zoom w-full h-full object-cover"/>
                <div className="absolute inset-0 bg-[#15348C]/40"/>
                <div className="absolute inset-0 flex flex-col items-center justify-center text-white p-3 text-center">
                  <CalendarDays size={20} className="text-[#8BD835]"/>
                  <div className="text-sm font-bold mt-1 leading-tight">{e.date}</div>
                </div>
              </div>
              <div className="col-span-2 p-5 flex flex-col">
                <h3 className="text-[#15348C] group-hover:text-[#6BBE32] transition-colors leading-snug" style={{fontSize:"1.1rem"}}>{e.title}</h3>
                <div className="mt-3 text-sm text-gray-600 flex items-center gap-1"><MapPin size={14} className="text-[#F57C20]"/> {e.city} — {e.venue}</div>
                <Link to="/contact" className="mt-4 inline-flex items-center gap-1 text-sm text-[#F57C20] font-semibold sg-link">Register →</Link>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
    <Footer/>
  </div>
);
